/**
 * Custom hook to handle organization switching while viewing org-scoped detail pages
 * Redirects to dashboard when organization changes to prevent errors
 * 
 * IMPORTANT: This hook checks for completion lock AND snapshot before redirecting.
 * If a user just completed an assessment (has lock or snapshot), redirects are blocked.
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOrganization } from '../context/OrganizationContext';

// Check if there's a valid completion lock that should block redirects
const hasCompletionLock = () => {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('assessment_completion_lock_')) {
        const data = JSON.parse(sessionStorage.getItem(key));
        // Check if not expired
        if (new Date(data.expiresAt) > new Date()) {
          console.log('[useOrgSwitchRedirect] Found valid completion lock:', key);
          return true;
        }
      }
    }
  } catch (e) {
    console.error('[useOrgSwitchRedirect] Error checking lock:', e);
  }
  return false;
};

// Check if there's a recent completion snapshot that should block redirects
const hasRecentCompletionSnapshot = () => {
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith('assessment_completion_') && !key.includes('_lock_')) {
        const data = JSON.parse(sessionStorage.getItem(key));
        const timestamp = data.createdAt || data.timestamp;
        const isRecent = Date.now() - timestamp < 5 * 60 * 1000; // 5 minutes
        if (isRecent) {
          console.log('[useOrgSwitchRedirect] Found recent completion snapshot:', key);
          return true;
        }
      }
    }
  } catch (e) {
    console.error('[useOrgSwitchRedirect] Error checking snapshot:', e);
  }
  return false;
};

export const useOrgSwitchRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeOrganization, loading: orgLoading } = useOrganization();
  const [initialOrgId, setInitialOrgId] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (orgLoading) {
      setIsReady(false);
      return;
    }
    
    if (!activeOrganization) return;
    
    if (initialOrgId === null) {
      // First load - store the initial organization
      setInitialOrgId(activeOrganization.organization_id);
      setIsReady(true);
    } else if (initialOrgId !== activeOrganization.organization_id) {
      // Organization changed - check if we should block redirect
      const isOnAssessmentPage = location.pathname.includes('/assessments/');
      const hasSuccessParam = location.search.includes('success=1');
      const hasLock = hasCompletionLock();
      const hasSnapshot = hasRecentCompletionSnapshot();
      
      if (isOnAssessmentPage && (hasSuccessParam || hasLock || hasSnapshot)) {
        console.log('[useOrgSwitchRedirect] Blocking redirect - completion in progress', { hasSuccessParam, hasLock, hasSnapshot });
        // Don't redirect, but update the initial org ID
        setInitialOrgId(activeOrganization.organization_id);
        return;
      }
      
      // Organization changed - redirect to assessments list immediately
      console.log('[useOrgSwitchRedirect] Organization changed, redirecting');
      setIsReady(false);
      navigate('/assessments', { replace: true });
    }
  }, [activeOrganization, orgLoading, initialOrgId, navigate, location.pathname, location.search]);

  return { isReady, activeOrganization, orgLoading };
};
